import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCalendar } from '@/components/TabIcons';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import {
  dateKeyFromDate,
  formatMonthHeader,
  getMonthGrid,
  navigateMonth,
} from '@/calendar/pwaCalendarUtils';
import {
  formatStayDate,
  isoDay,
  resourceStayState,
  resourcesForSelector,
  stayNights,
  type DraftResource,
  type ResourceStayState,
} from '@/lib/reservationStay';

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const;

export type ReservationPrefill = {
  checkIn?: string | null;
  checkOut?: string | null;
  resourceId?: string | null;
};

type Draft = {
  visitorName: string;
  suggestion: { checkIn: string | null; checkOut: string | null; resourceIds: string[] };
  resources: DraftResource[];
};

type Props = {
  conversationId: string;
  prefill: ReservationPrefill | null;
  onClose: () => void;
  onCreated: (message: string) => void;
};

export function ChatReservationSheet({ conversationId, prefill, onClose, onCreated }: Props) {
  const { post } = useSession();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(() => now.getFullYear());
  const [month, setMonth] = useState(() => now.getMonth() + 1);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const applied = useRef(false);

  const prefillCheckIn = isoDay(prefill?.checkIn);
  const prefillCheckOut = isoDay(prefill?.checkOut);
  const prefillResourceId = prefill?.resourceId?.trim() || null;

  useEffect(() => {
    let cancelled = false;
    void post<Draft>(`/api/console/conversations/${conversationId}/reservation-draft`, {})
      .then((data) => {
        if (!cancelled) setDraft(data);
      })
      .catch((e) => {
        if (!cancelled) setLoadErr(e instanceof ApiError ? e.message : 'No se pudo armar la reserva');
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, post]);

  useEffect(() => {
    if (!draft || applied.current) return;
    applied.current = true;
    const fromPrefill =
      prefillCheckIn && prefillCheckOut && stayNights(prefillCheckIn, prefillCheckOut) >= 2;
    const nextIn = fromPrefill ? prefillCheckIn : draft.suggestion.checkIn;
    const nextOut = fromPrefill ? prefillCheckOut : draft.suggestion.checkOut;
    if (nextIn && nextOut && stayNights(nextIn, nextOut) >= 2) {
      setCheckIn(nextIn);
      setCheckOut(nextOut);
      setYear(Number(nextIn.slice(0, 4)));
      setMonth(Number(nextIn.slice(5, 7)));
    }
  }, [draft, prefillCheckIn, prefillCheckOut]);

  const suggestionIds = useMemo(() => {
    const ids = [...(draft?.suggestion.resourceIds ?? [])];
    if (prefillResourceId) ids.unshift(prefillResourceId);
    return [...new Set(ids)];
  }, [draft, prefillResourceId]);

  const nights = checkIn && checkOut ? stayNights(checkIn, checkOut) : 0;
  const rangeReady = Boolean(checkIn && checkOut && nights >= 2);

  const options = useMemo(() => {
    if (!draft || !rangeReady) return [];
    return resourcesForSelector(draft.resources);
  }, [draft, rangeReady]);

  useEffect(() => {
    if (!rangeReady) {
      setResourceId(null);
      return;
    }
    setResourceId((current) => {
      if (current && options.some((r) => r.id === current)) return current;
      const fromChat = suggestionIds.find((id) => options.some((r) => r.id === id));
      return fromChat ?? null;
    });
  }, [rangeReady, options, suggestionIds]);

  const selected = options.find((r) => r.id === resourceId) ?? null;
  const stayState: ResourceStayState =
    selected && checkIn && checkOut ? resourceStayState(selected, checkIn, checkOut) : 'taken';
  const weeks = getMonthGrid(year, month);
  const visitorName = draft?.visitorName || 'Visitante';

  function pickDay(key: string) {
    setConfirmOpen(false);
    setSubmitErr(null);
    if (!checkIn || checkOut) {
      setCheckIn(key);
      setCheckOut(null);
      return;
    }
    if (key <= checkIn) {
      setCheckIn(key);
      setCheckOut(null);
      return;
    }
    setCheckOut(key);
  }

  function dayClass(key: string): string {
    const classes = ['chat-reserve__day'];
    if (checkIn && key === checkIn) classes.push('chat-reserve__day--start');
    if (checkOut && key === checkOut) classes.push('chat-reserve__day--end');
    if (checkIn && checkOut && key > checkIn && key < checkOut) classes.push('chat-reserve__day--mid');
    return classes.join(' ');
  }

  async function submit() {
    if (!checkIn || !checkOut || !resourceId) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const result = await post<{ mode: 'created' | 'confirmed'; resourceName: string }>(
        `/api/console/conversations/${conversationId}/reservations`,
        { resourceId, checkIn, checkOut }
      );
      const verb = result.mode === 'confirmed' ? 'Reserva confirmada' : 'Reserva pendiente de seña';
      onCreated(`${verb}: ${result.resourceName}, ${formatStayDate(checkIn)} al ${formatStayDate(checkOut)}.`);
      onClose();
    } catch (e) {
      setSubmitErr(e instanceof ApiError ? e.message : 'No se pudo crear la reserva');
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pwa-info-sheet chat-reserve" role="dialog" aria-modal="true" aria-labelledby="chat-reserve-title">
      <button type="button" className="pwa-info-sheet__backdrop" aria-label="Cerrar reserva" onClick={onClose} />
      <div className="pwa-info-sheet__panel chat-reserve__panel">
        <header className="pwa-info-sheet__header">
          <h2 id="chat-reserve-title" className="pwa-info-sheet__title">
            <IconCalendar width={18} height={18} aria-hidden />
            Reservar
          </h2>
          <button type="button" className="pwa-info-sheet__close" aria-label="Cerrar" onClick={onClose}>
            ×
          </button>
        </header>

        {loadErr ? <p className="pwa-err chat-reserve__note">{loadErr}</p> : null}
        {!draft && !loadErr ? <p className="pwa-muted chat-reserve__note">Leyendo el chat…</p> : null}

        {draft ?
          <>
            <div className="chat-reserve__cal">
              <div className="chat-reserve__month">
                <button
                  type="button"
                  className="chat-reserve__nav"
                  aria-label="Mes anterior"
                  onClick={() => {
                    const n = navigateMonth(year, month, 'prev');
                    setYear(n.year);
                    setMonth(n.month);
                  }}
                >
                  ‹
                </button>
                <span>{formatMonthHeader(year, month)}</span>
                <button
                  type="button"
                  className="chat-reserve__nav"
                  aria-label="Mes siguiente"
                  onClick={() => {
                    const n = navigateMonth(year, month, 'next');
                    setYear(n.year);
                    setMonth(n.month);
                  }}
                >
                  ›
                </button>
              </div>
              <div className="chat-reserve__weekdays">
                {WEEKDAYS.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              {weeks.map((week, i) => (
                <div key={i} className="chat-reserve__week">
                  {week.map((cell) => {
                    const key = dateKeyFromDate(cell.date);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={[
                          dayClass(key),
                          cell.isCurrentMonth ? '' : 'chat-reserve__day--muted',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => pickDay(key)}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <p className="pwa-muted chat-reserve__note">
              {checkIn && checkOut && nights < 2 ?
                'El mínimo del rango es de 2 días.'
              : checkIn && checkOut ?
                `${formatStayDate(checkIn)} → ${formatStayDate(checkOut)} (${nights} noches)`
              : 'Elegí entrada y salida. El mínimo es de 2 días.'}
            </p>

            {rangeReady ?
              options.length === 0 ?
                <p className="chat-reserve__note">No hay recursos para reservar.</p>
              : <label className="chat-reserve__select">
                  Recurso
                  <select
                    value={resourceId ?? ''}
                    onChange={(e) => {
                      setResourceId(e.target.value || null);
                      setConfirmOpen(false);
                      setSubmitErr(null);
                    }}
                  >
                    <option value="">Elegí un recurso</option>
                    {options.map((r) => {
                      const state = checkIn && checkOut ? resourceStayState(r, checkIn, checkOut) : 'free';
                      const suffix =
                        state === 'airbnb' ? ' · bloqueado en Airbnb'
                        : state === 'taken' ? ' · ocupado'
                        : '';
                      return (
                        <option key={r.id} value={r.id}>
                          {r.name}{suffix}
                        </option>
                      );
                    })}
                  </select>
                </label>
            : null}

            {rangeReady && selected && stayState === 'airbnb' ?
              <p className="chat-reserve__warn">
                Este recurso está bloqueado en Airbnb. Podés reservarlo igual: la reserva de
                Interactive Talker queda por encima.
              </p>
            : null}
            {rangeReady && selected && stayState === 'taken' ?
              <p className="chat-reserve__note">Ese recurso no está libre en ese rango.</p>
            : null}

            {submitErr ? <p className="pwa-err chat-reserve__note">{submitErr}</p> : null}

            <div className="chat-reserve__actions">
              <button
                type="button"
                className="pwa-btn pwa-btn--primary"
                disabled={!rangeReady || !selected || stayState === 'taken' || submitting}
                onClick={() => setConfirmOpen(true)}
              >
                Reservar
              </button>
            </div>
          </>
        : null}
      </div>
      {confirmOpen && selected && checkIn && checkOut ?
        <div className="chat-reserve__confirm" role="dialog" aria-labelledby="chat-reserve-confirm-title">
          <button
            type="button"
            className="chat-reserve__confirm-backdrop"
            aria-label="Volver"
            disabled={submitting}
            onClick={() => setConfirmOpen(false)}
          />
          <div className="chat-reserve__confirm-card">
            <h3 id="chat-reserve-confirm-title">Confirmar reserva</h3>
            <p>
              ¿Reservar <strong>{selected.name}</strong> del {formatStayDate(checkIn)} al{' '}
              {formatStayDate(checkOut)} para <strong>{visitorName}</strong>?
            </p>
            {stayState === 'airbnb' ?
              <p>Queda por encima del bloqueo de Airbnb. En el calendario se ve ese detalle.</p>
            : null}
            <div className="chat-reserve__actions">
              <button
                type="button"
                className="pwa-btn pwa-btn--primary"
                disabled={submitting}
                onClick={() => void submit()}
              >
                {submitting ? 'Reservando…' : 'Aceptar'}
              </button>
              <button
                type="button"
                className="pwa-btn pwa-btn--secondary"
                disabled={submitting}
                onClick={() => setConfirmOpen(false)}
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      : null}
    </div>
  );
}
